/**
 * This file contains authentication parameters. Contents of this file
 * is roughly the same across other MSAL.js libraries. These parameters
 * are used to initialize Angular and MSAL Angular configurations in
 * in app.module.ts file.
 */

import { LogLevel, Configuration, BrowserCacheLocation } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: environment.clientId, // This is the ONLY mandatory field that you need to supply.
    authority: environment.authorityUrl + environment.tenantId, // Defaults to "https://login.microsoftonline.com/common"
    redirectUri: environment.redirectUrl, // Points to window.location.origin. You must register this URI on Azure portal/App Registration.
  },
  cache: {
    // tslint:disable-next-line: max-line-length
    cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: isIE, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback(logLevel: LogLevel, message: string) {
        console.log(message);
      },
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false
    }
  }
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */

export const protectedResources = {
  agreementStatusApi: {
    endpoint: environment.serviceURL + environment.getAgreementStatus,
    scopes: environment.scopeUri,
  }, getCountryApi: {
    endpoint: environment.serviceURL + environment.getCountry,
    scopes: environment.scopeUri,
  }, getDonorAgencyApi: {
    endpoint: environment.serviceURL + environment.getDonorAgency,
    scopes: environment.scopeUri,
  }, getActivityApi: {
    endpoint: environment.serviceURL + environment.getActivity,
    scopes: environment.scopeUri,
  }, getTechnicalAreaApi: {
    endpoint: environment.serviceURL + environment.getTechnicalArea,
    scopes: environment.scopeUri,
  }, getContactApi: {
    endpoint: environment.serviceURL + environment.getContact,
    scopes: environment.scopeUri,
  }, getRegionApi: {
    endpoint: environment.serviceURL + environment.getRegion,
    scopes: environment.scopeUri,
  }, postAwardInformationApi: {
    endpoint: environment.serviceURL + environment.postAwardInformation,
    scopes: environment.scopeUri,
  }, postAgreementDetailsApi: {
    endpoint: environment.serviceURL + environment.postAgreementDetails,
    scopes: environment.scopeUri,
  }, getAgreementsApi: {
    endpoint: environment.serviceURL + environment.getAgreements,
    scopes: environment.scopeUri,
  }, getDonorContactsApi: {
    endpoint: environment.serviceURL + environment.getDonorContacts,
    scopes: environment.scopeUri,
  }, postSaveAgreementCountryApi: {
    endpoint: environment.serviceURL + environment.postSaveAgreementCountry,
    scopes: environment.scopeUri,
  }, postSaveAgreementRegionApi: {
    endpoint: environment.serviceURL + environment.postSaveAgreementRegion,
    scopes: environment.scopeUri,
  }, postSaveDonorContactApi: {
    endpoint: environment.serviceURL + environment.postSaveDonorContact,
    scopes: environment.scopeUri,
  }, postAgreementTechnicalAreaApi: {
    endpoint: environment.serviceURL + environment.postAgreementTechnicalArea,
    scopes: environment.scopeUri,
  }, postCountryImplementationApi: {
    endpoint: environment.serviceURL + environment.postCountryImplementation,
    scopes: environment.scopeUri,
  }, deleteCountryImplementationApi: {
    endpoint: environment.serviceURL + environment.deleteCountryImplementation,
    scopes: environment.scopeUri,
  }, getAgreementInformationDetailsApi: {
    endpoint: environment.serviceURL + environment.getAgreementInformationDetails,
    scopes: environment.scopeUri,
  }, postAgreementActivityApi: {
    endpoint: environment.serviceURL + environment.postAgreementActivity,
    scopes: environment.scopeUri,
  }, deleteAgreementCountryApi: {
    endpoint: environment.serviceURL + environment.deleteAgreementCountry,
    scopes: environment.scopeUri,
  }, deleteAgreementRegionApi: {
    endpoint: environment.serviceURL + environment.deleteAgreementRegion,
    scopes: environment.scopeUri,
  }, deleteDonorContactApi: {
    endpoint: environment.serviceURL + environment.deleteDonorContact,
    scopes: environment.scopeUri,
  }, postSaveAgreementDonorContactApi: {
    endpoint: environment.serviceURL + environment.postSaveAgreementDonorContact,
    scopes: environment.scopeUri,
  }, deleteAgreementDonorContactApi: {
    endpoint: environment.serviceURL + environment.deleteAgreementDonorContact,
    scopes: environment.scopeUri,
  }, deleteAgreementActivityApi: {
    endpoint: environment.serviceURL + environment.deleteAgreementActivity,
    scopes: environment.scopeUri,
  }, deleteTechnicalAreaApi: {
    endpoint: environment.serviceURL + environment.deleteTechnicalArea,
    scopes: environment.scopeUri,
  }, getDonorsListApi: {
    endpoint: environment.serviceURL + environment.getDonorsList,
    scopes: environment.scopeUri,
  }, getDonorInformationApi: {
    endpoint: environment.serviceURL + environment.getDonorInformation,
    scopes: environment.scopeUri,
  }, postDonorInformationApi: {
    endpoint: environment.serviceURL + environment.postDonorInformation,
    scopes: environment.scopeUri,
  }, postDonorContactApi: {
    endpoint: environment.serviceURL + environment.postDonorContact,
    scopes: environment.scopeUri,
  }, getDeliverablesType: {
    endpoint: environment.serviceURL + environment.getDeliverablesType,
    scopes: environment.scopeUri,
  }, getNotificationFrequency: {
    endpoint: environment.serviceURL + environment.getNotificationFrequency,
    scopes: environment.scopeUri,
  }, getDocumentsType: {
    endpoint: environment.serviceURL + environment.getDocumentType,
    scopes: environment.scopeUri,
  }, getDocumentDetails: {
    endpoint: environment.serviceURL + environment.getDocumentDetails,
    scopes: environment.scopeUri,
  }, saveDocuments: {
    endpoint: environment.serviceURL + environment.saveDocument,
    scopes: environment.scopeUri,
  }, deleteDocuments: {
    endpoint: environment.serviceURL + environment.deleteDocument,
    scopes: environment.scopeUri,
  }, getAwardContacts: {
    endpoint: environment.serviceURL + environment.getAwardContacts,
    scopes: environment.scopeUri,
  }, deleteDonorInfoEntryContacts: {
    endpoint: environment.serviceURL + environment.deleteDonorInfoEntryContacts,
    scopes: environment.scopeUri,
  }, uploadDocument: {
    endpoint: environment.serviceURL + environment.uploadDocument,
    scopes: environment.scopeUri,
  }, downloadDocument: {
    endpoint: environment.serviceURL + environment.downloadDocument,
    scopes: environment.scopeUri,
  }, getAllCountry: {
    endpoint: environment.serviceURL + environment.getAllCountry,
    scopes: environment.scopeUri,
  }, getRegionCountry: {
    endpoint: environment.serviceURL + environment.getRegionCountry,
    scopes: environment.scopeUri,
  }, deleteDocumentFile: {
    endpoint: environment.serviceURL + environment.deleteDocumentFile,
    scopes: environment.scopeUri,
  }, getDeliverableInformation: {
    endpoint: environment.serviceURL + environment.getDeliverableInformation,
    scopes: environment.scopeUri,
  }, postDeliverableInformation: {
    endpoint: environment.serviceURL + environment.postDeliverableInformation,
    scopes: environment.scopeUri,
  }, deleteDeliverableInformation: {
    endpoint: environment.serviceURL + environment.deleteDeliverableInformation,
    scopes: environment.scopeUri,
  }, getDeliverablePeriod: {
    endpoint: environment.serviceURL + environment.getDeliverablePeriod,
    scopes: environment.scopeUri,
  }, getDeliverablePeriodValue: {
    endpoint: environment.serviceURL + environment.getDeliverablePeriodValue,
    scopes: environment.scopeUri,
  }, getFunder: {
    endpoint: environment.serviceURL + environment.getFunder,
    scopes: environment.scopeUri,
  }, saveNotes: {
    endpoint: environment.serviceURL + environment.saveNotes,
    scopes: environment.scopeUri,
  }, getNotes: {
    endpoint: environment.serviceURL + environment.getNotes,
    scopes: environment.scopeUri,
  }, deleteNotes: {
    endpoint: environment.serviceURL + environment.deleteNotes,
    scopes: environment.scopeUri,
  }, getPriorApprovalsType: {
    endpoint: environment.serviceURL + environment.getPriorApprovalsType,
    scopes: environment.scopeUri,
  }, getPriorApprovalsInformation: {
    endpoint: environment.serviceURL + environment.getPriorApprovalsInformation,
    scopes: environment.scopeUri,
  }, savePriorApprovalsInformation: {
    endpoint: environment.serviceURL + environment.savePriorApprovalsInformation,
    scopes: environment.scopeUri,
  }, deletePriorApprovals: {
    endpoint: environment.serviceURL + environment.deletePriorApprovals,
    scopes: environment.scopeUri,
  }, getAwardAudit: {
    endpoint: environment.serviceURL + environment.getAwardAudit,
    scopes: environment.scopeUri,
  }, getAuditDocuments: {
    endpoint: environment.serviceURL + environment.getAuditDocuments,
    scopes: environment.scopeUri,
  }, getAllUsers: {
    endpoint: environment.serviceURL + environment.getAllUsers,
    scopes: environment.scopeUri,
  }, postDataMigration: {
    endpoint: environment.serviceURL + environment.postDataMigration,
    scopes: environment.scopeUri,
  }, getDeliverableDetails: {
    endpoint: environment.serviceURL + environment.getDeliverableDetails,
    scopes: environment.scopeUri,
  }, postDeliverableDetails: {
    endpoint: environment.serviceURL + environment.postDeliverableDetails,
    scopes: environment.scopeUri,
  }, deleteDeliverableDetails: {
    endpoint: environment.serviceURL + environment.deleteDeliverableDetails,
    scopes: environment.scopeUri,
  }, getAdminCheck: {
    endpoint: environment.serviceURL + environment.getAdminCheck,
    scopes: environment.scopeUri,
  }, getIsAwardYearUsed: {
    endpoint: environment.serviceURL + environment.getIsAwardYearUsed,
    scopes: environment.scopeUri,
  }, documentDataMigration: {
    endpoint: environment.serviceURL + environment.documentDataMigration,
    scopes: environment.scopeUri,
  }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: environment.loginScopes
};
